<script>
  import { tick } from "svelte";
  import { formatNumber } from "../utils/format.js";

  // The pending_approval payload from the messages poll:
  //   { id, tool_call_id, function_name, arguments, kind, evidence, requested_at }
  export let approval;
  export let deciding = false;      // optimistic disable while a decision is in flight
  export let driftBanner = false;   // set true after a 409 data_changed re-render
  export let onApprove = () => {};  // onApprove()
  export let onReject = () => {};   // onReject(reason)

  let reason = "";
  // The "No / Reject" path reveals a reason field; the human types why so the
  // agent can adapt. "Yes / Approve" needs no reason.
  let showRejectReason = false;

  function chooseReject() {
    showRejectReason = true;
  }

  function submitReject() {
    onReject(reason);
  }

  $: kind = approval?.kind;
  $: evidence = approval?.evidence || {};
  $: verify = evidence.verify || null;
  $: verifyAccepted = verify ? verify.accepted === true : true;
  // Terminal approve is blocked when the fresh verify does not pass — the human
  // must not complete a generation against failing validation.
  $: approveDisabled = deciding || (kind === "terminal" && !verifyAccepted);

  $: previewRows = Array.isArray(evidence.deleted_preview) ? evidence.deleted_preview : [];
  $: tailCount = evidence.tail_count || previewRows.length;
  $: moreCount = Math.max(tailCount - previewRows.length, 0);
  // post_transaction ("create") gate: the proposed spending row comes from the
  // tool arguments (product/amount/description/date), NOT from delete evidence.
  $: createRow = approval?.arguments?.row || {};

  // The gate is phrased as a yes/no question the human answers.
  function header() {
    if (kind === "destructive") {
      const fromOrder = evidence.from_row_order ?? "?";
      const later = Math.max((tailCount || 1) - 1, 0);
      return `Delete row ${fromOrder} and ${later} later ${later === 1 ? "row" : "rows"}? (cannot be undone)`;
    }
    if (kind === "create") {
      const product = createRow.product || "a spending row";
      return `Post a spending row — ${product}?`;
    }
    const genId = approval?.arguments?.generation_id ?? approval?.arguments?.["generation_id"];
    return `Complete generation${genId ? ` #${genId}` : ""}?`;
  }

  function verifyErrors() {
    if (!verify || !Array.isArray(verify.errors)) {
      return [];
    }
    return verify.errors;
  }

  // Keyboard shortcuts for the ONE pending gate: A approves, R opens the reject
  // reason and focuses it. Exactly one ApprovalCard is ever mounted (the live
  // gate — the read-only archive suppresses it), so this window listener is
  // unambiguous and unmounts with the card. Ignored while a decision is in
  // flight, while a modifier is held, or while the user is typing in a field —
  // so typing the reject reason (or steering the agent) never fires Approve.
  function isTypingTarget(el) {
    if (!el) return false;
    const tag = el.tagName;
    return el.isContentEditable || tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT";
  }

  function onWindowKeydown(event) {
    if (deciding || event.metaKey || event.ctrlKey || event.altKey) return;
    if (isTypingTarget(event.target)) return;
    const key = event.key.toLowerCase();
    if (key === "a") {
      if (!approveDisabled) {
        event.preventDefault();
        onApprove();
      }
    } else if (key === "r") {
      event.preventDefault();
      chooseReject();
      tick().then(() => document.getElementById(`approval-reason-${approval?.id}`)?.focus());
    }
  }
</script>

<svelte:window on:keydown={onWindowKeydown} />

<div
  class="border rounded-lg p-4 my-2 shadow-sm {kind === 'destructive'
    ? 'border-danger-border/40 bg-danger-bg/20'
    : kind === 'create'
      ? 'border-brand-200 bg-brand-50/40'
      : 'border-neutral-200 bg-white'}"
  data-testid="approval-card"
>
  <div class="flex items-center justify-between gap-3 mb-2">
    <h5 class="text-sm font-bold text-neutral-900" data-testid="approval-header">
      {header()}
    </h5>
    <span
      class="inline-flex items-center px-2 py-0.5 rounded-full text-[0.62rem] font-bold uppercase tracking-wider border {kind ===
      'destructive'
        ? 'bg-danger-bg text-danger-text border-danger-border/20'
        : kind === 'create'
          ? 'bg-brand-50 text-brand-600 border-brand-200/40'
          : 'bg-info-bg text-info-text border-info-border/20'}"
    >
      {kind === "destructive" ? "Destructive" : kind === "create" ? "Spending" : "Terminal"} · needs approval
    </span>
  </div>

  {#if driftBanner}
    <p
      class="bg-warning-bg text-warning-text border border-warning-border/30 rounded px-2.5 py-1.5 text-xs mb-3"
      role="alert"
      data-testid="approval-drift-banner"
    >
      The data changed since this was requested — please re-review before deciding.
    </p>
  {/if}

  {#if kind === "destructive"}
    <p
      class="bg-danger-bg text-danger-text border border-danger-border/20 rounded px-2.5 py-1.5 text-xs mb-3"
      role="alert"
    >
      Approving permanently deletes these {tailCount} spending {tailCount === 1 ? "row" : "rows"}. This cannot be undone.
    </p>

    {#if previewRows.length > 0}
      <div class="overflow-x-auto border border-neutral-200 rounded-lg mb-2 bg-white">
        <table class="w-full border-collapse text-xs text-left">
          <thead>
            <tr class="bg-neutral-50 border-b border-neutral-200">
              <th class="p-2 font-bold text-neutral-700">Order</th>
              <th class="p-2 font-bold text-neutral-700">Product / description</th>
              <th class="p-2 font-bold text-neutral-700">Amount</th>
              <th class="p-2 font-bold text-neutral-700">Balance</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-neutral-100">
            {#each previewRows as row}
              <tr data-testid="approval-preview-row">
                <td class="p-2 font-medium">{row.row_order ?? "—"}</td>
                <td class="p-2 text-neutral-900">{row.description || "—"}</td>
                <td class="p-2 font-mono text-neutral-900">{formatNumber(row.amount)}</td>
                <td class="p-2 font-mono font-semibold text-neutral-900">{formatNumber(row.balance)}</td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
      {#if moreCount > 0}
        <p class="text-[0.7rem] text-neutral-500 mb-2" data-testid="approval-more">…and {moreCount} more</p>
      {/if}
    {/if}
  {:else if kind === "create"}
    <p
      class="bg-brand-50 text-brand-700 border border-brand-200/40 rounded px-2.5 py-1.5 text-xs mb-3"
      role="status"
    >
      The agent proposes posting this spending row. Approve to add it, or reject with a reason.
    </p>
    <div class="overflow-x-auto border border-neutral-200 rounded-lg mb-2 bg-white" data-testid="approval-create-details">
      <table class="w-full border-collapse text-xs text-left">
        <tbody class="divide-y divide-neutral-100">
          <tr>
            <td class="p-2 font-semibold text-neutral-600 w-32">Product</td>
            <td class="p-2 text-neutral-900" data-testid="approval-create-product">{createRow.product ?? "—"}</td>
          </tr>
          {#if createRow.amount != null}
            <tr>
              <td class="p-2 font-semibold text-neutral-600">Amount</td>
              <td class="p-2 font-mono text-neutral-900">{createRow.amount}</td>
            </tr>
          {/if}
          {#if createRow.description}
            <tr>
              <td class="p-2 font-semibold text-neutral-600">Description</td>
              <td class="p-2 text-neutral-900">{createRow.description}</td>
            </tr>
          {/if}
          {#if createRow.date_time}
            <tr>
              <td class="p-2 font-semibold text-neutral-600">Date</td>
              <td class="p-2 font-mono text-neutral-900">{createRow.date_time}</td>
            </tr>
          {/if}
        </tbody>
      </table>
    </div>
  {:else}
    {#if verifyAccepted}
      <p
        class="bg-success-bg text-success-text border border-success-border/20 rounded px-2.5 py-1.5 text-xs mb-3"
        role="status"
        data-testid="approval-verify-pass"
      >
        All checks pass — the generation is ready to complete.
      </p>
    {:else}
      <div class="mb-3" data-testid="approval-verify-fail">
        <p class="bg-danger-bg text-danger-text border border-danger-border/20 rounded px-2.5 py-1.5 text-xs mb-2" role="alert">
          Verification failed — completion is blocked until these are resolved:
        </p>
        <ul class="list-disc list-inside space-y-0.5 text-xs text-danger-text">
          {#each verifyErrors() as error}
            <li data-testid="approval-verify-error">{error}</li>
          {/each}
          {#if verifyErrors().length === 0}
            <li>Generation did not pass verification.</li>
          {/if}
        </ul>
      </div>
    {/if}
  {/if}

  <div class="mt-3 space-y-3" data-testid="approval-decision">
    <p class="text-xs font-semibold text-neutral-700">Approve this action?</p>

    <!-- YES / Approve path -->
    <div class="flex flex-wrap gap-2 items-center">
      <button
        type="button"
        class="btn-primary py-1 text-xs"
        data-testid="approval-approve"
        disabled={approveDisabled}
        title={kind === "terminal" && !verifyAccepted ? "Verification must pass before completion can be approved" : ""}
        on:click={onApprove}
      >
        {deciding ? "Working…" : "Yes, approve (A)"}
      </button>
      <button
        type="button"
        class="btn-secondary py-1 text-xs bg-danger-bg text-danger-text border border-danger-border/20"
        data-testid="approval-reject-toggle"
        disabled={deciding}
        aria-expanded={showRejectReason}
        on:click={chooseReject}
      >
        No, reject… (R)
      </button>
    </div>

    {#if kind === "terminal" && !verifyAccepted}
      <p class="text-[0.7rem] text-danger-text" data-testid="approval-approve-blocked">
        Approve is disabled while verification is failing.
      </p>
    {/if}

    <!-- NO / Reject path: reveals the reason field so the agent can adapt. The
         field stays in the DOM for the reveal, but the actual reject submit is
         guarded so a bare "No" click opens it first. -->
    {#if showRejectReason}
      <div class="space-y-2 border-t border-neutral-200 pt-3" data-testid="approval-reject-panel">
        <label class="form-label" for="approval-reason-{approval?.id}">Why are you rejecting? (the agent sees this)</label>
        <textarea
          id="approval-reason-{approval?.id}"
          class="form-input text-xs"
          rows="2"
          data-testid="approval-reason"
          bind:value={reason}
          placeholder="Tell the agent why, so it can adapt…"
        ></textarea>
        <div class="flex justify-end">
          <button
            type="button"
            class="btn-secondary py-1 text-xs bg-danger-bg text-danger-text border border-danger-border/20"
            data-testid="approval-reject"
            disabled={deciding}
            on:click={submitReject}
          >
            {deciding ? "Working…" : "Submit rejection"}
          </button>
        </div>
      </div>
    {/if}
  </div>
</div>
